/**
 * Community wall — the API contract. The types now live in
 * `shared/types/community.ts` (the real server serves them) and the request
 * schemas in `shared/validation/community.schema.ts`; this file re-exports
 * them so every import in the feature (and in `welcome`) keeps working, and
 * adds the UI labels.
 *
 * Endpoints (all authenticated, all viewer-scoped): see shared/types/community.ts.
 * Text only — no post or reply carries a photo. Posting and commenting wait
 * for the three promises (403 FORBIDDEN, details.reason "PLEDGE_REQUIRED").
 *
 * The pinned welcome post (id "ll-welcome") is served by the `welcome`
 * feature in exactly this CommunityPost shape.
 */
import type { ReactionKind, ReportReason, PostType } from "@shared/types/community";

export {
  COMMUNITY_TOPICS,
  HEAVIER_TOPICS,
  REACTION_KINDS,
  SENSITIVE_TOPICS,
  TOPIC_DESCRIPTIONS,
  isHeavierTopic,
  orderBoards,
  topicFromSlug,
  topicSlug,
  topicsForMarket,
} from "@shared/constants/community";

export {
  ANONYMOUS_AUTHOR_ID,
  PLEDGE_REQUIRED_REASON,
  type AnsweredRequest,
  type AnsweredState,
  type BoardList,
  type CircleRef,
  type CirclePage,
  type CommentPage,
  type CommunityAuthor,
  type CommunityBadgeId,
  type CommunityBoard,
  type CommunityCircle,
  type CommunityComment,
  type CommunityMe,
  type CommunityPost,
  type CommunityTopic,
  type CreateCommentRequest,
  type CreatePostRequest,
  type CreateReportRequest,
  type DeletedState,
  type JoinCircleRequest,
  type ListPostsQuery,
  type PostAudience,
  type PostCta,
  type PostPage,
  type PostStatus,
  type PostType,
  type ReactRequest,
  type ReactionCounts,
  type ReactionKind,
  type ReactionState,
  type ReportReason,
  type ReportReceipt,
  type SaveRequest,
  type SavedState,
  type UpdateCommentRequest,
  type UpdateCommunityMeRequest,
  type UpdatePostRequest,
} from "@shared/types/community";

/* ---------- labels shared by the UI and the README ---------- */

export const REACTION_LABELS: Record<ReactionKind, { emoji: string; label: string }> = {
  hugs: { emoji: "❤️", label: "Sending hugs" },
  beenThere: { emoji: "🙌", label: "Been there" },
  helpful: { emoji: "💡", label: "Helpful" },
};

export const POST_TYPE_LABELS: Record<PostType, string> = {
  share: "Share",
  advice: "Ask for advice",
  win: "Celebrate a win",
};

export const REPORT_REASON_LABELS: Record<ReportReason, { label: string; hint: string }> = {
  unkind: { label: "Unkind or bullying", hint: "Shaming, name-calling or piling on" },
  unsafeAdvice: { label: "Unsafe health advice", hint: "Dosing, diagnoses or risky home remedies" },
  childPrivacy: { label: "Shows a child's private details", hint: "Full name, school or location" },
  spam: { label: "Spam or selling", hint: "Ads, links or direct selling" },
  atRisk: { label: "Someone may need urgent help", hint: "A parent or child could be in danger" },
  other: { label: "Something else", hint: "Tell us in a few words" },
};
